import { zodResolver } from '@hookform/resolvers/zod';
import {
  IInvoiceRespSchema,
  ISharedInvoicePaymentUI,
  PaymentGatewayConfigurations,
  SharedInvoicePaymentExistingMethod,
  SharedInvoicePaymentExistingMethodSchema,
  SharedInvoicePaymentSchemaUI,
} from '@monetize/types/app';
import {
  FunctionComponent as FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { handleApiErrorToast } from '../../../api/axios';
import {
  useGetPaymentMethodsByInvoiceShareId,
  usePayInvoiceWithPaymentMethodForInvoiceShare,
} from '../../../api/shareService';
import {
  MButton,
  MCenterModal,
  MPageLoader,
  MRadio,
  MRadioGroup,
  MStack,
  MText,
} from '../../../components/Monetize';
import { logger } from '../../../services/logger';
import {
  getPaymentAmountLimits,
  stripeTypeToPaymentMethodType,
  validatePaymentAmountLimits,
} from '../../../utils/payment.utils';
import { usePayInvoiceWithStripeAnonymous } from '../usePayInvoiceWithStripeAnonymous';
import { PendingVerificationBanner } from './PendingVerificationBanner';
import { SharedInvoiceFormExistingPaymentMethod } from './SharedInvoiceFormExistingPaymentMethod';
import { SharedInvoiceFormNewPaymentMethod } from './SharedInvoiceFormNewPaymentMethod';

interface SharedInvoiceFormFieldsProps {
  allowPartialPayment: boolean;
  invoice: IInvoiceRespSchema;
  paymentMethodId?: string;
  invoiceSecretId: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isOpen: boolean;
  publicKey: string;
  setupIntent: string;
  tenantId: string;
  paymentConfiguration?: PaymentGatewayConfigurations;
  onClose: () => void;
}

export const SharedInvoiceFormFields: FC<SharedInvoiceFormFieldsProps> = ({
  allowPartialPayment,
  invoice,
  paymentMethodId,
  invoiceSecretId,
  isDisabled,
  isLoading,
  isOpen,
  publicKey,
  tenantId,
  paymentConfiguration,
  onClose,
}: SharedInvoiceFormFieldsProps) => {
  // Fetch existing payment methods, default to first if available
  const { data: paymentMethods, isLoading: isPaymentMethodsLoading } =
    useGetPaymentMethodsByInvoiceShareId(
      { tenantId, invoiceSecretId },
      {
        refetchOnWindowFocus: false,
      },
    );

  useEffect(() => {
    if (paymentMethods && paymentMethods?.length > 0) {
      setPaymentType('existing');
    }
  }, [paymentMethods]);

  const { mutateAsync: createPaymentExistingPaymentMethod } =
    usePayInvoiceWithPaymentMethodForInvoiceShare();

  const hasPaymentMethods = paymentMethods && paymentMethods?.length > 0;
  const [paymentType, setPaymentType] = useState<'existing' | 'new'>(() =>
    hasPaymentMethods ? 'existing' : 'new',
  );
  const [isSaving, setIsSaving] = useState(() => false);

  const {
    handleFormSubmit: savePaymentMethodAndPayment,
    isSavePaymentLoading,
    requiresVerification,
    stripeLoading,
  } = usePayInvoiceWithStripeAnonymous({
    allowPartialPayment,
    amountDue: invoice.amountDue,
    invoiceSecretId,
    onSuccess: onClose,
    tenantId,
  });

  const existingPaymentMethodFormState =
    useForm<SharedInvoicePaymentExistingMethod>({
      resolver: zodResolver(SharedInvoicePaymentExistingMethodSchema),
      mode: 'onChange',
      defaultValues: {
        amount: invoice.amountDue,
        paymentMethodId: paymentMethodId || '',
      },
    });

  const newPaymentMethodFormState = useForm<ISharedInvoicePaymentUI>({
    resolver: zodResolver(SharedInvoicePaymentSchemaUI),
    mode: 'onChange',
    defaultValues: {
      amount: invoice.amountDue,
      stripePublicKey: publicKey,
      billingDetails: {
        fullName: invoice.billingContact.fullName || '',
        email: invoice.billingContact.email || '',
      },
      stripeData: {
        empty: true,
        complete: false,
        type: 'card',
      },
    },
  });

  const getExistingMethodAmountLimits = useCallback(() => {
    if (!paymentConfiguration) {
      return null;
    }
    const selectedId =
      existingPaymentMethodFormState.getValues('paymentMethodId');
    const selectedMethod = paymentMethods?.find(({ id }) => id === selectedId);
    if (!selectedMethod) {
      return null;
    }
    return getPaymentAmountLimits(
      paymentConfiguration,
      selectedMethod.paymentMethodType,
      invoice.currency,
    );
  }, [
    paymentConfiguration,
    paymentMethods,
    existingPaymentMethodFormState,
    invoice.currency,
  ]);

  const existingAmount = existingPaymentMethodFormState.watch('amount');
  const existingPaymentMethodId =
    existingPaymentMethodFormState.watch('paymentMethodId');
  const newAmount = newPaymentMethodFormState.watch('amount');
  const newStripeType = newPaymentMethodFormState.watch('stripeData.type');

  const hasAmountLimitError = useMemo(() => {
    if (paymentType === 'existing') {
      const selectedMethod = paymentMethods?.find(
        ({ id }) => id === existingPaymentMethodId,
      );
      if (!selectedMethod) {
        return false;
      }
      const limits = getPaymentAmountLimits(
        paymentConfiguration,
        selectedMethod.paymentMethodType,
        invoice.currency,
      );
      return !!validatePaymentAmountLimits(
        limits,
        existingAmount,
        invoice.currency,
      );
    }
    const methodType = stripeTypeToPaymentMethodType(newStripeType);
    if (!methodType) {
      return false;
    }
    const limits = getPaymentAmountLimits(
      paymentConfiguration,
      methodType,
      invoice.currency,
    );
    return !!validatePaymentAmountLimits(limits, newAmount, invoice.currency);
  }, [
    paymentType,
    paymentMethods,
    existingPaymentMethodId,
    existingAmount,
    newStripeType,
    newAmount,
    paymentConfiguration,
    invoice.currency,
  ]);

  const onError = (errs: any, event: any) => {
    logger.error('Error submitting payment form', errs, event);
  };

  const handleExistingPaymentMethodFormSubmit = async (
    body: SharedInvoicePaymentExistingMethod,
  ) => {
    const amountLimits = getExistingMethodAmountLimits();
    const limitError = validatePaymentAmountLimits(
      amountLimits,
      body.amount,
      invoice.currency,
    );
    if (limitError) {
      existingPaymentMethodFormState.setError('amount', {
        type: 'manual',
        message: limitError,
      });
      return;
    }

    try {
      setIsSaving(true);
      await createPaymentExistingPaymentMethod({
        tenantId,
        invoiceSecretId,
        body,
      });
      onClose();
    } catch (ex) {
      handleApiErrorToast(ex);
    } finally {
      setIsSaving(false);
    }
  };

  const handleNewPaymentMethodFormSubmit = async (
    body: ISharedInvoicePaymentUI,
  ) => {
    if (paymentConfiguration) {
      const stripeType = body.stripeData.type;
      const methodType = stripeTypeToPaymentMethodType(stripeType);
      if (methodType) {
        const amountLimits = getPaymentAmountLimits(
          paymentConfiguration,
          methodType,
          invoice.currency,
        );
        const limitError = validatePaymentAmountLimits(
          amountLimits,
          body.amount,
          invoice.currency,
        );
        if (limitError) {
          newPaymentMethodFormState.setError('amount', {
            type: 'manual',
            message: limitError,
          });
          return;
        }
      }
    }

    // Parent has try catch, so this call-site should never fail
    setIsSaving(true);
    await savePaymentMethodAndPayment(body);
    setIsSaving(false);
  };

  return (
    <MCenterModal
      isOpen={isOpen}
      onClose={onClose}
      modalTitle="Pay Invoice"
      size="lg"
      closeOnOverlayClick={false}
      closeOnEsc={false}
      renderFooter={() => (
        <MStack gap={4} direction="row" align="center" justify="right" flex={1}>
          {requiresVerification && (
            <MButton
              type="button"
              onClick={onClose}
              variant="cancel"
              minW="auto"
            >
              Close
            </MButton>
          )}

          {!requiresVerification && (
            <>
              <MButton onClick={onClose} variant="cancel" minW="auto">
                Cancel
              </MButton>
              <MButton
                type="submit"
                form="payment-form"
                variant="primary"
                isLoading={
                  stripeLoading || isSavePaymentLoading || isLoading || isSaving
                }
                isDisabled={
                  stripeLoading ||
                  isSavePaymentLoading ||
                  isLoading ||
                  isSaving ||
                  hasAmountLimitError
                }
                minW="auto"
              >
                Pay
              </MButton>
            </>
          )}
        </MStack>
      )}
    >
      {isPaymentMethodsLoading && <MPageLoader maxH={100} />}
      {!isPaymentMethodsLoading && (
        <form
          id="payment-form"
          onSubmit={
            paymentType === 'existing'
              ? existingPaymentMethodFormState.handleSubmit(
                  handleExistingPaymentMethodFormSubmit,
                  onError,
                )
              : newPaymentMethodFormState.handleSubmit(
                  handleNewPaymentMethodFormSubmit,
                  onError,
                )
          }
        >
          <PendingVerificationBanner
            requiresVerification={requiresVerification}
          />

          {!requiresVerification && (
            <>
              {/* EXISTING PAYMENT METHOD */}
              {hasPaymentMethods && (
                <MRadioGroup
                  value={paymentType}
                  onChange={(value) =>
                    setPaymentType(value as 'existing' | 'new')
                  }
                >
                  <MStack mb={2}>
                    <MRadio value="existing">
                      <MText fontSize="sm" ml={2} fontWeight="bold">
                        Existing Payment Method
                      </MText>
                    </MRadio>
                    {paymentType === 'existing' && (
                      <SharedInvoiceFormExistingPaymentMethod
                        allowPartialPayment={allowPartialPayment}
                        formState={existingPaymentMethodFormState}
                        invoice={invoice}
                        isDisabled={isDisabled}
                        paymentMethods={paymentMethods || []}
                        paymentConfiguration={paymentConfiguration}
                      />
                    )}
                    <MRadio value="new">
                      <MText fontSize="sm" ml={2} fontWeight="bold">
                        New Payment Method
                      </MText>
                    </MRadio>
                  </MStack>
                </MRadioGroup>
              )}
              {(!hasPaymentMethods || paymentType === 'new') && (
                <SharedInvoiceFormNewPaymentMethod
                  allowPartialPayment={allowPartialPayment}
                  formState={newPaymentMethodFormState}
                  invoice={invoice}
                  isDisabled={isDisabled}
                  paymentConfiguration={paymentConfiguration}
                />
              )}
            </>
          )}
        </form>
      )}
    </MCenterModal>
  );
};
